import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { I18nPipe, ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { DownloadService } from 'src/app/services/download.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

interface AttachmentItem {
  id: string;
  name: string;
  originalFullName: string;
  url: string;
  type: string;
  size: number;
  extension: string;
}

interface IssueRepairDetail {
  id: string;
  issueNumber: string;
  name: string;
  type: string;
  issueTime: number;
  explain: string;
  userName: string;
  mobile?: string;
  deviceType: string;
  tenantName?: string;
  status: 'pending' | 'processing' | 'completed' | 'rejected';
  attachmentInfos?: AttachmentItem[];
}

@Component({
  selector: 'app-issue-repair-detail',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzFormModule,
    NzDividerModule,
    NzInputModule,
    NzIconModule,
    NzTagModule,
    NzSpinModule,
    NzModalModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './issue-repair-detail.component.html',
  styleUrls: ['./issue-repair-detail.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IssueRepairDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly httpService = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly downloadService = inject(DownloadService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  loading = false;
  issueData: IssueRepairDetail | null = null;
  previewVisible = false;
  currentAttachment: AttachmentItem | null = null;

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadIssueDetail(id);
    } else {
      this.message.error(this.i18n.fanyi('issue-repair.detail.invalid-id'));
      this.goBack();
    }
  }

  loadIssueDetail(id: string): void {
    this.loading = true;

    // 使用真實API調用
    this.httpService
      .post(`${ConstantUtil.BASE_REPAIR_DETAIL}/${id}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response: any) => {
          this.issueData = response.data;
          this.processAttachmentInfos();
        },
        error: error => {
          this.message.error(this.i18n.fanyi('issue-repair.detail.load-failed'));
          this.goBack();
        }
      });
  }
  processAttachmentInfos(): void {
    if (!this.issueData) return;
  }
  goBack(): void {
    this.router.navigate(['/repairs']);
  }
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  previewAttachment(attachment: AttachmentItem): void {
    this.currentAttachment = attachment;
    this.previewVisible = true;
  }

  previewDefaultAttachment(): void {
    this.message.info(this.i18n.fanyi('issue-repair.detail.preview-attachment'));
  }

  closePreview(): void {
    this.previewVisible = false;
    this.currentAttachment = null;
  }
  downloadFile(fileId: string, fileName?: string): void {
    this.downloadService.downloadFile(fileId, fileName);
  }

  downloadDefaultAttachment(): void {
    this.message.success(this.i18n.fanyi('issue-repair.detail.start-download'));
  }

  isImage(fileType: string): boolean {
    return fileType.toLowerCase().includes('image');
  }

  isPdf(fileType: string): boolean {
    return fileType.toLowerCase().includes('pdf');
  }

  getFileUrl(fileId: string) {
    return this.downloadService.downloadFile(fileId);
  }
}
